import { useEffect, useState, useCallback } from "react";
import api, { fmtError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Cloud, CloudOff, ExternalLink, RefreshCw, Upload, Power } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/format";

// Pretty-print file size in KB / MB.
function fmtBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DriveBackupCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/drive-backup/status");
      setStatus(data);
    } catch (err) { toast.error(fmtError(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Surface ?drive=connected / ?drive=error&msg=... from the OAuth callback
  // so the admin sees a friendly toast right after Google bounces them back.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const drv = params.get("drive");
    if (!drv) return;
    if (drv === "connected") {
      toast.success("Google Drive connected. Backups will start uploading tonight.");
    } else if (drv === "error") {
      toast.error(`Drive connect failed: ${params.get("msg") || "unknown"}`);
    }
    params.delete("drive"); params.delete("msg");
    const next = window.location.pathname + (params.toString() ? `?${params.toString()}` : "");
    window.history.replaceState({}, "", next);
    load();
  }, [load]);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/admin/drive-backup/connect");
      window.location.href = data.auth_url;
    } catch (err) {
      toast.error(fmtError(err));
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Drive? Future backups will stop uploading until you reconnect.")) return;
    try {
      await api.post("/admin/drive-backup/disconnect");
      toast.success("Disconnected. Existing backups in Drive are NOT deleted.");
      load();
    } catch (err) { toast.error(fmtError(err)); }
  };

  const uploadNow = async () => {
    setUploading(true);
    try {
      const { data } = await api.post("/admin/drive-backup/upload-now");
      toast.success(`Uploaded ${data.upload?.name || "backup"} (${fmtBytes(data.upload?.size)})`);
      load();
      if (listOpen) loadFiles();
    } catch (err) { toast.error(fmtError(err)); }
    finally { setUploading(false); }
  };

  const loadFiles = async () => {
    setFilesLoading(true);
    try {
      const { data } = await api.get("/admin/drive-backup/list");
      setFiles(data.files || []);
    } catch (err) { toast.error(fmtError(err)); }
    finally { setFilesLoading(false); }
  };

  const toggleList = () => {
    if (!listOpen) loadFiles();
    setListOpen(!listOpen);
  };

  if (loading) {
    return (
      <Card className="rounded-3xl shadow-soft">
        <CardContent className="p-6 text-sm text-muted-foreground">Loading Drive status…</CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-3xl shadow-soft" data-testid="drive-backup-card">
      <CardContent className="p-6">
        <div className="flex items-start gap-3">
          <div className={`h-10 w-10 rounded-2xl grid place-items-center shrink-0 ${status?.connected ? "bg-primary-50 text-primary" : "bg-muted text-muted-foreground"}`}>
            {status?.connected ? <Cloud className="h-5 w-5" /> : <CloudOff className="h-5 w-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Cloud backup</p>
            <p className="font-display text-lg font-extrabold tracking-tight mt-0.5">Google Drive</p>
            <p className="text-sm text-muted-foreground mt-1">
              Every nightly backup (02:00 UTC) auto-uploads to <strong>{status?.folder_name}</strong> in the connected Google account. Files older than <strong>{status?.retention_days} days</strong> are auto-deleted. Survives any container/redeploy disk loss.
            </p>
          </div>
        </div>

        {!status?.configured && (
          <p className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-2xl p-3" data-testid="drive-backup-not-configured">
            Google Drive credentials missing. Set <code>GOOGLE_DRIVE_CLIENT_ID</code> and <code>GOOGLE_DRIVE_CLIENT_SECRET</code> in <code>backend/.env</code>.
          </p>
        )}

        {status?.configured && !status?.connected && (
          <div className="mt-4">
            <Button
              onClick={connect}
              disabled={connecting}
              className="rounded-2xl tap"
              data-testid="drive-backup-connect"
            >
              <Cloud className="h-4 w-4 mr-1.5" /> {connecting ? "Redirecting…" : "Connect Google Drive"}
            </Button>
            <p className="text-[11px] text-muted-foreground mt-2">
              You'll be sent to Google to sign in once. The CRM only sees backup files it creates — it cannot read the rest of your Drive.
            </p>
          </div>
        )}

        {status?.connected && (
          <>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-muted-foreground">Connected as</p>
                <p className="font-semibold truncate" data-testid="drive-connected-email">{status.connected_by_email || status.connected_by_name || "—"}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-muted-foreground">Uploads so far</p>
                <p className="font-semibold">{status.uploads_count || 0}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-muted-foreground">Last upload</p>
                <p className="font-semibold truncate" data-testid="drive-last-upload">
                  {status.last_upload ? fmtDateTime(status.last_upload.uploaded_at) : "—"}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-muted-foreground">Last size</p>
                <p className="font-semibold">{fmtBytes(status.last_upload?.size)}</p>
              </div>
            </div>

            {status.last_upload_error && (
              <p className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-xl p-2.5">
                Last upload failed @ {fmtDateTime(status.last_upload_error.at)}: <span className="font-mono">{status.last_upload_error.message}</span>
              </p>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                onClick={uploadNow}
                disabled={uploading}
                className="rounded-2xl tap"
                data-testid="drive-upload-now"
              >
                <Upload className="h-4 w-4 mr-1.5" /> {uploading ? "Uploading…" : "Snapshot + upload now"}
              </Button>
              <Button
                onClick={toggleList}
                variant="outline"
                className="rounded-2xl tap"
                data-testid="drive-list-toggle"
              >
                <RefreshCw className="h-4 w-4 mr-1.5" />
                {listOpen ? "Hide cloud files" : "Show cloud files"}
              </Button>
              {status.last_upload?.web_view_link && (
                <Button
                  asChild
                  variant="outline"
                  className="rounded-2xl tap"
                >
                  <a href={status.last_upload.web_view_link} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4 mr-1.5" /> Open in Drive
                  </a>
                </Button>
              )}
              <Button
                onClick={disconnect}
                variant="ghost"
                className="rounded-2xl tap text-red-600 hover:bg-red-50"
                data-testid="drive-disconnect"
              >
                <Power className="h-4 w-4 mr-1.5" /> Disconnect
              </Button>
            </div>

            {listOpen && (
              <div className="mt-4 rounded-2xl border border-border/60 max-h-72 overflow-y-auto">
                {filesLoading && <p className="p-4 text-sm text-muted-foreground">Loading…</p>}
                {!filesLoading && files.length === 0 && <p className="p-4 text-sm text-muted-foreground">No backups in Drive yet.</p>}
                {!filesLoading && files.map((f) => (
                  <a
                    key={f.id}
                    href={f.webViewLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between gap-3 px-3 py-2.5 border-b last:border-b-0 border-border/40 hover:bg-muted/50 transition"
                    data-testid={`drive-file-${f.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{f.name}</p>
                      <p className="text-[11px] text-muted-foreground">{fmtDateTime(f.createdTime)} · {fmtBytes(Number(f.size) || 0)}</p>
                    </div>
                    <Badge variant="outline" className="text-[10px]">Drive</Badge>
                  </a>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
